import { Link } from "react-router-dom";
import { Play, Check, Cloud, Server } from "lucide-react";
import { Poster, ProgressLine } from "@/components/Poster";
import { epLabel, fmtRuntime, pct } from "@/lib/api";

const SourceBadge = ({ source }) => (
  <span className="flex items-center gap-1 rounded bg-white/90 px-1.5 py-0.5 font-mono text-[9px] uppercase text-zinc-700 shadow-sm backdrop-blur">
    {source === "plex" ? <Server size={9} /> : <Cloud size={9} />}{source}
  </span>
);

export const VideoCard = ({ video, index = 0, wide = false }) => {
  const p = video.progress;
  const sub = video.kind === "episode" ? `${video.show} · ${epLabel(video)}` : [video.year, fmtRuntime(video.duration)].filter(Boolean).join(" · ");
  const wideImg = video.kind === "episode" ? "still" : "backdrop";
  return (
    <div className="group animate-rise" style={{ animationDelay: `${Math.min(index, 20) * 30}ms` }}>
      <Link to={`/watch/${video.id}`} data-testid={`video-card-${video.id}`} className="block">
        <div className={`relative overflow-hidden rounded-md border border-zinc-200/70 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${wide ? "aspect-video" : "aspect-[2/3]"}`}>
          <Poster id={video.id} type={wide ? wideImg : "poster"} alt={video.title} className="h-full w-full transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
          <div className="absolute left-2 top-2 flex gap-1">
            <SourceBadge source={video.source} />
            {video.resolution && <span className="rounded bg-zinc-900/80 px-1.5 py-0.5 font-mono text-[9px] text-white">{video.resolution}</span>}
          </div>
          {p?.watched && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white shadow" data-testid={`video-watched-${video.id}`}><Check size={13} strokeWidth={3} /></span>}
          <span className="absolute bottom-3 right-3 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full bg-zinc-900 text-white opacity-0 shadow-lg transition-[opacity,transform] duration-200 group-hover:translate-y-0 group-hover:opacity-100">
            <Play size={16} fill="currentColor" className="ml-0.5" />
          </span>
          {!p?.watched && <ProgressLine value={pct(p)} className="absolute bottom-0 left-0" />}
        </div>
        <p className="mt-3 truncate text-sm font-medium text-zinc-900">{video.title}</p>
      </Link>
      <p className="truncate font-mono text-[11px] text-zinc-500">{sub || video.container?.toUpperCase()}</p>
    </div>
  );
};

export const VideoGrid = ({ videos, wide = false, testid = "video-grid" }) => (
  <div className={`grid gap-x-5 gap-y-8 ${wide ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"}`} data-testid={testid}>
    {videos.map((v, i) => <VideoCard key={v.id} video={v} index={i} wide={wide} />)}
  </div>
);

export const EmptyState = ({ title, text, action, testid = "empty-state" }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-white/60 px-8 py-16" data-testid={testid}>
    <p className="font-heading text-2xl font-bold tracking-tight">{title}</p>
    <p className="mt-2 max-w-md text-sm text-zinc-500">{text}</p>
    {action && <div className="mt-6">{action}</div>}
  </div>
);
